/**
 * Live session engine — the conversation state machine, extracted out of the
 * React component so it stops living inside a render closure.
 *
 * Why this exists: LiveScreen used to juggle ~15 `useRef`s to survive
 * re-renders, and its WebSocket callback was captured once when the connect
 * effect ran — so `handleWs` saw `msgs`, `youCode`, and `guestCode` frozen at
 * mount time. That produced real bugs:
 *   - the "Speaking" highlight never fired (msgs was the empty mount array),
 *   - switching the guest language mid-call mislabelled every later bubble.
 *
 * Here the whole thing is a plain class. It owns the WebSocket, the mic, the
 * TTS queue, retry/backoff, and the message list. State is held in mutable
 * fields (always current) and published through an immutable snapshot that a
 * React view subscribes to via `useSyncExternalStore`. All external
 * collaborators (ws, mic, audio, api) are injected, so the engine is unit
 * testable without a browser.
 */
import { closeMessage } from "../ws-close";
import type { WsEvent } from "../ws";
import type { MicEvent } from "../audio/mic";
import type { AudioSink } from "./tts";
import type { DemoReplyResponse, UserOut } from "../api";

export type Phase = "partial" | "final" | "spoken";
export type Msg = {
  id: string;
  who: "you" | "guest";
  srcLang: string;
  dstLang: string;
  src: string;
  dst: string;
  phase: Phase;
  turn_id?: number;
  /** Finalised but no TTS will ever play for this turn (server said
   *  tts_pending=false, the turn was abandoned, or it timed out). */
  unspoken?: boolean;
};
export type Capture = "idle" | "connecting" | "listening" | "trouble";

/** Immutable snapshot the view renders. A new object is produced on any change. */
export type LiveState = {
  msgs: Msg[];
  capture: Capture;
  hearing: boolean;
  level: number;
  speakingId: string | null;
  errBanner: string | null;
  youCode: string;
  guestCode: string;
};

/**
 * Demo-mode config. When present, after the owner's translated TTS finishes,
 * the engine calls `POST /demo/reply` for an LLM-drafted guest reply and plays
 * it through the same audio queue (mic stays half-duplex-muted meanwhile).
 */
export type LiveDemoMode = {
  speakerCode: string;
  listenerCode: string;
  voiceGender?: "female" | "male";
};

export interface WsLike {
  open(): Promise<boolean>;
  close(): void;
  sendUttStart(id: string): void;
  sendUttEnd(id: string): void;
  sendPcm(pcm: Int16Array): void;
  sendSetOtherLang(lang: string): void;
}

export interface MicLike {
  start(): Promise<void>;
  stop(): Promise<void>;
  /** Re-arm segmentation (discard a half-captured utterance) without teardown. */
  reset(): void;
}

export interface LiveDeps {
  /** Build a WsClient bound to the given event sink. */
  createWs(onEvent: (e: WsEvent) => void): WsLike;
  /** Build a MicCapture (with its VAD) bound to the given event sink. */
  createMic(onEvent: (e: MicEvent) => void): MicLike;
  /** Serialized TTS playback sink. */
  audio: AudioSink;
  /** PATCH /me — used when the guest language changes mid-call. */
  patchMe(patch: { other_lang_override: string }): Promise<UserOut>;
  /** POST /demo/reply — demo mode only. */
  demoReply(body: {
    speaker_text: string;
    speaker_language: string;
    listener_language: string;
    voice_gender: "female" | "male";
  }): Promise<DemoReplyResponse>;
  /** Persist the updated profile after a guest-language change. */
  onUser?(u: UserOut): void;
  /** Fired once per finalised owner turn (turn cap enforcement in demo). */
  onFinalTurn?(): void;
  now?(): number;
  setTimeout?(fn: () => void, ms: number): ReturnType<typeof setTimeout>;
  clearTimeout?(t: ReturnType<typeof setTimeout>): void;
}

export interface LiveConfig {
  youCode: string;
  guestCode: string;
  demoMode?: LiveDemoMode;
}

const UTT_MAX_DURATION_MS = 80_000;
const MAX_RETRIES = 5;
// A bubble stuck in "partial" with no partial/final activity for this long is
// abandoned client-side (finalised as unspoken, or removed if empty). Normal
// finals land within ~2 s of utt_end; the server's own TTS drain allows 5 s.
const TURN_ABANDON_MS = 8_000;
// Demo mode: the guest reply normally plays when the owner's TTS finishes. If
// that TTS never arrives (unspoken turn, synthesis failure), play the reply
// anyway after this long so its "…" placeholder can't blink forever.
const DEMO_REPLY_FALLBACK_MS = 12_000;

export class LiveEngine {
  private state: LiveState;
  private listeners = new Set<() => void>();

  // runtime collaborators
  private ws: WsLike | null = null;
  private mic: MicLike | null = null;

  // half-duplex / utterance bookkeeping
  private userPaused = false;
  // Count of TTS playback jobs in flight. A boolean here un-muted the mic too
  // early: with back-to-back TTS (owner turn + demo guest reply, or two queued
  // turns) the first job's completion cleared the flag while later audio was
  // still audible, letting speaker output re-enter the mic pipeline.
  private ttsJobs = 0;
  private uttId: string | null = null;
  private uttStartedAt = 0;
  private openByRole = new Map<string, string>();
  // role → limbo watchdog for its open partial bubble (see TURN_ABANDON_MS)
  private limboTimers = new Map<string, ReturnType<typeof setTimeout>>();

  // reconnect
  private retryCount = 0;
  private cancelled = false;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;

  // mic startup race guard (mirrors the old micEpochRef fix)
  private micStarting = false;
  private micEpoch = 0;

  // demo mode
  private demoReplyInflight: { id: string; promise: Promise<DemoReplyResponse | null> } | null = null;
  private openerFired = false;
  private firstUtteranceSeen = false;

  private readonly now: () => number;
  private readonly setTimeoutFn: (fn: () => void, ms: number) => ReturnType<typeof setTimeout>;
  private readonly clearTimeoutFn: (t: ReturnType<typeof setTimeout>) => void;

  constructor(private readonly deps: LiveDeps, config: LiveConfig) {
    this.now = deps.now ?? (() => Date.now());
    this.setTimeoutFn = deps.setTimeout ?? ((fn, ms) => setTimeout(fn, ms));
    this.clearTimeoutFn = deps.clearTimeout ?? ((t) => clearTimeout(t));
    this.demoMode = config.demoMode;
    this.state = {
      msgs: [],
      capture: "idle",
      hearing: false,
      level: 0.04,
      speakingId: null,
      errBanner: null,
      youCode: config.youCode,
      guestCode: config.guestCode,
    };
  }

  private demoMode?: LiveDemoMode;

  // ─── external store surface ───────────────────────────────────────────
  subscribe = (cb: () => void): (() => void) => {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  };
  getState = (): LiveState => this.state;

  private set(patch: Partial<LiveState>): void {
    this.state = { ...this.state, ...patch };
    for (const l of this.listeners) l();
  }

  private appendMsg(m: Msg): void {
    this.set({ msgs: [...this.state.msgs, m] });
  }
  private patchMsg(id: string, p: Partial<Msg>): void {
    this.set({ msgs: this.state.msgs.map((x) => (x.id === id ? { ...x, ...p } : x)) });
  }

  private get youCode(): string { return this.state.youCode; }
  private get guestCode(): string { return this.state.guestCode; }

  /** Dismiss the current error banner (X button in the view). */
  clearError(): void { this.set({ errBanner: null }); }

  // ─── lifecycle ────────────────────────────────────────────────────────
  /** Open the WebSocket and (in demo mode) pre-warm the AI opener. */
  start(): void {
    this.cancelled = false;
    this.connect();
    this.maybeFireOpener();
  }

  /** Tear everything down. Safe to call multiple times. */
  stop(): void {
    this.cancelled = true;
    if (this.retryTimer) { this.clearTimeoutFn(this.retryTimer); this.retryTimer = null; }
    this.clearAllLimbo();
    try { this.ws?.close(); } catch { /* ignore */ }
    this.ws = null;
    this.stopMic();
    this.stopTts();
  }

  private connect = (): void => {
    if (this.cancelled) return;
    this.set({ capture: "connecting" });
    if (this.retryCount > 0) {
      this.set({ errBanner: `Connection lost. Reconnecting... (Attempt ${this.retryCount}/${MAX_RETRIES})` });
    }
    const ws = this.deps.createWs((ev) => this.handleWs(ev));
    ws.open().then((ok) => {
      if (this.cancelled) { try { ws.close(); } catch { /* ignore */ } return; }
      if (!ok) {
        this.set({ errBanner: "You're signed out — please sign in again.", capture: "idle" });
        return;
      }
      this.ws = ws;
    });
  };

  private scheduleReconnect(): void {
    if (this.cancelled) return;
    this.retryCount += 1;
    if (this.retryCount > MAX_RETRIES) {
      this.set({
        errBanner: "Connection failed after multiple attempts. Please check your network and tap Connect.",
        capture: "idle",
      });
      this.retryCount = 0;
      return;
    }
    const delay = Math.min(750 * Math.pow(2, this.retryCount), 15000);
    this.retryTimer = this.setTimeoutFn(this.connect, delay);
  }

  // ─── WebSocket event handling ─────────────────────────────────────────
  private handleWs(ev: WsEvent): void {
    switch (ev.type) {
      case "connected":
        this.set({ errBanner: null });
        this.retryCount = 0;
        if (this.userPaused) {
          // Reconnect after a user pause (e.g. server idle-close while paused).
          // Keep the session warm but don't re-arm the mic — wait for a tap.
          this.set({ capture: "idle" });
          break;
        }
        this.set({ capture: "listening" });
        this.startMic().catch(() => { /* mic warm-up failure is non-fatal here */ });
        break;

      case "utt_accepted":
        ev.turns.forEach((t) => {
          const who: "you" | "guest" = t.label === "owner" ? "you" : "guest";
          const openId = this.openByRole.get(t.label);
          if (openId) this.patchMsg(openId, { turn_id: t.turn_id, who });
        });
        break;

      case "partial": {
        const role = ev.role;
        const who: "you" | "guest" = role === "owner" ? "you" : "guest";
        if (role === "owner" && ev.src_text) this.firstUtteranceSeen = true;
        const dstCode = who === "you" ? this.guestCode : this.youCode;
        let id = this.openByRole.get(role);
        if (!id) {
          id = `m-${this.now()}-${role}`;
          this.openByRole.set(role, id);
          this.appendMsg({
            id, who,
            srcLang: who === "you" ? this.youCode : this.guestCode,
            dstLang: dstCode,
            src: ev.src_text, dst: pickTrans(ev.translations, dstCode),
            phase: "partial",
          });
        } else {
          this.patchMsg(id, { src: ev.src_text, dst: pickTrans(ev.translations, dstCode) });
        }
        this.touchLimbo(role);
        break;
      }

      case "final": {
        const role = ev.role;
        if (ev.turn_id < 0) {
          // Terminal notice for a turn the server dropped (duplicate final,
          // or a final whose Turn row was lost): close the open bubble
          // instead of leaving it partial forever. Never appends a bubble,
          // never counts as a real turn.
          this.abandonTurn(role);
          break;
        }
        this.clearLimbo(role);
        const who: "you" | "guest" = role === "owner" ? "you" : "guest";
        const dstCode = who === "you" ? this.guestCode : this.youCode;
        if (role === "owner" && ev.src_text) this.firstUtteranceSeen = true;
        // tts_pending=false means the server will never synthesize this turn
        // (same-language pass-through, empty translation, …) — mark it so the
        // UI doesn't imply audio is still coming.
        const unspoken = ev.tts_pending ? undefined : true;
        const id = this.openByRole.get(role);
        if (id) {
          this.patchMsg(id, {
            phase: "final", src: ev.src_text,
            dst: pickTrans(ev.translations, dstCode), turn_id: ev.turn_id, unspoken,
          });
          this.openByRole.delete(role);
        } else {
          this.appendMsg({
            id: `m-${this.now()}-${role}-final`, who,
            srcLang: who === "you" ? this.youCode : this.guestCode, dstLang: dstCode,
            src: ev.src_text, dst: pickTrans(ev.translations, dstCode),
            phase: "final", turn_id: ev.turn_id, unspoken,
          });
        }
        if (this.demoMode && role === "owner" && ev.src_text) {
          this.kickDemoReply(ev.src_text);
          // No owner TTS will play for this turn, so the normal guest-reply
          // trigger (the owner `tts_audio` completion) never fires — play the
          // reply as soon as it's ready instead of leaving its placeholder
          // hanging.
          if (unspoken) void this.playDemoGuestReply();
        }
        if (role === "owner") { try { this.deps.onFinalTurn?.(); } catch { /* ignore */ } }
        break;
      }

      case "tts_header":
        this.forceCloseUtt();
        break;

      case "tts_audio": {
        const turnId = ev.turn_id;
        const role = ev.role;
        this.beginTts();
        // Reads the CURRENT message list — the old closure froze `msgs` at mount
        // so this lookup always missed and the highlight never fired.
        const matchId = this.state.msgs.find((m) => m.turn_id === turnId)?.id ?? null;
        if (matchId) this.set({ speakingId: matchId });
        this.deps.audio.enqueue(ev.wav).then(async () => {
          if (this.state.speakingId === matchId) this.set({ speakingId: null });
          if (this.demoMode && role === "owner") {
            try { await this.playDemoGuestReply(); } catch { /* ignore */ }
          }
          this.endTts();
        });
        break;
      }

      case "lang_changed":
        break;

      case "error":
        this.set({ errBanner: ev.message || ev.code });
        break;

      case "session_ended":
        this.set({ capture: "idle" });
        break;

      case "closed": {
        const { msg, retry } = closeMessage(ev.code, ev.reason);
        this.stopMic();
        if (retry) {
          if (this.retryCount === 0) this.set({ errBanner: "Connection dropped. Reconnecting..." });
          this.scheduleReconnect();
        } else {
          if (msg) this.set({ errBanner: msg });
          this.set({ capture: "idle" });
        }
        break;
      }
    }
  }

  // ─── mic ──────────────────────────────────────────────────────────────
  private async startMic(): Promise<void> {
    if (this.mic || this.micStarting) return;
    this.micStarting = true;
    const epoch = this.micEpoch;
    try {
      const mic = this.deps.createMic((e) => this.handleMic(e));
      await mic.start();
      if (this.micEpoch !== epoch) {
        // A stopMic() raced with the slow async start() (e.g. user tapped Pause
        // during Silero/getUserMedia warm-up) — throw this mic away.
        mic.stop().catch(() => { /* ignore */ });
        return;
      }
      this.mic = mic;
    } finally {
      this.micStarting = false;
    }
  }

  private stopMic(): void {
    this.micEpoch += 1; // invalidate any in-flight startMic()
    this.micStarting = false;
    const mic = this.mic;
    this.mic = null;
    mic?.stop().catch(() => { /* ignore */ });
    this.set({ level: 0.04, hearing: false });
  }

  private handleMic(e: MicEvent): void {
    const ws = this.ws;
    if (!ws) return;
    if (this.ttsJobs > 0) return; // half-duplex: drop everything during TTS
    switch (e.type) {
      case "start": {
        const utt = `u-${this.now()}`;
        this.uttId = utt;
        this.uttStartedAt = this.now();
        this.openByRole.clear();
        ws.sendUttStart(utt);
        this.set({ hearing: true });
        break;
      }
      case "pcm":
        // Only stream frames once an utterance is actually open. If the guest
        // started talking *during* TTS playback, that onset was muted (dropped
        // below), so the segmenter can emit trailing `pcm` with no matching
        // `utt_start` — forwarding those would send the server orphan audio.
        if (!this.uttId) return;
        ws.sendPcm(e.data);
        if (this.now() - this.uttStartedAt > UTT_MAX_DURATION_MS) this.forceCloseUtt();
        break;
      case "end": {
        const utt = this.uttId;
        this.uttId = null;
        this.set({ hearing: false });
        if (utt) ws.sendUttEnd(utt);
        break;
      }
      case "level":
        this.set({ level: e.rms01 });
        break;
    }
  }

  private forceCloseUtt(): void {
    const ws = this.ws;
    const utt = this.uttId;
    if (ws && utt) ws.sendUttEnd(utt);
    this.uttId = null;
    this.set({ hearing: false });
  }

  private stopTts(): void {
    this.ttsJobs = 0;
    this.deps.audio.stop();
  }

  private beginTts(): void {
    this.ttsJobs += 1;
  }

  /**
   * Called when one TTS playback job finishes. Only when the LAST in-flight
   * job ends does the half-duplex mute drop and the mic segmenter re-arm —
   * discarding any utterance half-captured while muted so the next real
   * speech onset opens a clean utterance instead of leaking mid-word `pcm`
   * frames with no `utt_start`.
   */
  private endTts(): void {
    this.ttsJobs = Math.max(0, this.ttsJobs - 1);
    if (this.ttsJobs === 0) this.mic?.reset();
  }

  // ─── turn limbo watchdog ──────────────────────────────────────────────
  /**
   * (Re)arm the abandon watchdog for `role`'s open bubble. Refreshed on every
   * partial; cleared by a real final. If it fires, the turn is abandoned the
   * same way a server terminal notice would — so a fragment whose final
   * evaporates (chopped utterance, Azure never committing) can't leave a
   * blinking partial bubble on screen forever.
   */
  private touchLimbo(role: string): void {
    this.clearLimbo(role);
    this.limboTimers.set(role, this.setTimeoutFn(() => {
      this.limboTimers.delete(role);
      this.abandonTurn(role);
    }, TURN_ABANDON_MS));
  }

  private clearLimbo(role: string): void {
    const t = this.limboTimers.get(role);
    if (t != null) {
      this.clearTimeoutFn(t);
      this.limboTimers.delete(role);
    }
  }

  private clearAllLimbo(): void {
    for (const role of [...this.limboTimers.keys()]) this.clearLimbo(role);
  }

  /**
   * Close `role`'s open partial bubble without a normal final. An empty
   * bubble — or one duplicating the previous final for the same speaker (the
   * server's dedup drop) — is removed; anything else is kept, finalised, and
   * marked unspoken so the UI stops implying audio is coming.
   */
  private abandonTurn(role: string): void {
    this.clearLimbo(role);
    const id = this.openByRole.get(role);
    if (!id) return;
    this.openByRole.delete(role);
    const msg = this.state.msgs.find((m) => m.id === id);
    if (!msg) return;
    const prev = [...this.state.msgs].reverse()
      .find((m) => m.id !== id && m.who === msg.who && m.phase !== "partial");
    const dup = !!msg.src && !!prev && normText(prev.src) === normText(msg.src);
    if (!msg.src || dup) {
      this.set({ msgs: this.state.msgs.filter((m) => m.id !== id) });
    } else {
      this.patchMsg(id, { phase: "final", unspoken: true });
    }
  }

  // ─── controls ─────────────────────────────────────────────────────────
  toggle(): void {
    const cap = this.state.capture;
    if (cap === "idle" || cap === "trouble") {
      this.userPaused = false;
      this.openByRole.clear();
      this.clearAllLimbo();
      this.set({ msgs: [], speakingId: null, errBanner: null });
      if (!this.ws) {
        this.openFresh();
      } else {
        this.startMic().catch(() => { /* ignore */ });
        this.set({ capture: "listening" });
      }
    } else {
      // Latch BEFORE stopping so a racing "connected" (reconnect in flight)
      // sees the paused state and skips startMic().
      this.userPaused = true;
      this.stopMic();
      this.set({ capture: "idle" });
    }
  }

  reset(): void {
    this.openByRole.clear();
    this.clearAllLimbo();
    this.demoReplyInflight = null;
    this.firstUtteranceSeen = false;
    this.retryCount = 0;
    if (this.retryTimer) { this.clearTimeoutFn(this.retryTimer); this.retryTimer = null; }
    this.set({ msgs: [], speakingId: null, errBanner: null });
    this.stopMic();
    try { this.ws?.close(); } catch { /* ignore */ }
    this.ws = null;
    this.userPaused = false;
    this.openFresh();
  }

  /**
   * Open a fresh socket for a manual (re)connect from toggle()/reset(). Unlike
   * the initial {@link connect} path this doesn't schedule retries, but it does
   * handle a failed open — otherwise the UI would sit on "Connecting…" forever
   * when `open()` resolves false (e.g. the token was cleared).
   */
  private openFresh(): void {
    this.set({ capture: "connecting" });
    const ws = this.deps.createWs((ev) => this.handleWs(ev));
    ws.open().then((ok) => {
      if (this.cancelled) { try { ws.close(); } catch { /* ignore */ } return; }
      if (!ok) {
        this.set({ errBanner: "You're signed out — please sign in again.", capture: "idle" });
        return;
      }
      this.ws = ws;
    });
  }

  async setGuest(code: string): Promise<void> {
    // Optimistically update the language the state machine stamps onto bubbles
    // so the very next partial/final is labelled correctly — this is the bug
    // the old frozen closure caused (new bubbles kept the old codes).
    this.set({ guestCode: code });
    try {
      const me = await this.deps.patchMe({ other_lang_override: code });
      this.deps.onUser?.(me);
      this.ws?.sendSetOtherLang(code);
    } catch (e) {
      this.set({ errBanner: e instanceof Error ? e.message : String(e) });
    }
  }

  // ─── demo mode ────────────────────────────────────────────────────────
  private maybeFireOpener(): void {
    const demo = this.demoMode;
    if (!demo || this.openerFired) return;
    this.openerFired = true;
    const id = `demo-opener-${this.now()}`;
    this.appendMsg({
      id, who: "guest",
      srcLang: demo.listenerCode, dstLang: demo.speakerCode,
      src: "", dst: "", phase: "partial",
    });
    this.deps.demoReply({
      speaker_text: "",
      speaker_language: demo.speakerCode,
      listener_language: demo.listenerCode,
      voice_gender: demo.voiceGender ?? "female",
    }).then(async (reply) => {
      if (this.firstUtteranceSeen) {
        this.set({ msgs: this.state.msgs.filter((m) => m.id !== id) });
        return;
      }
      this.patchMsg(id, { src: reply.reply_text, dst: reply.back_translation, phase: "final" });
      this.set({ speakingId: id });
      this.beginTts();
      try { await this.deps.audio.enqueue(base64ToArrayBuffer(reply.audio_base64)); }
      catch { /* ignore */ }
      finally {
        this.endTts();
        if (this.state.speakingId === id) this.set({ speakingId: null });
      }
    }).catch(() => {
      this.set({ msgs: this.state.msgs.filter((m) => m.id !== id) });
    });
  }

  private kickDemoReply(srcText: string): void {
    const demo = this.demoMode;
    if (!demo || this.demoReplyInflight) return;
    const fakeId = `demo-${this.now()}`;
    this.appendMsg({
      id: fakeId, who: "guest",
      srcLang: demo.listenerCode, dstLang: demo.speakerCode,
      src: "", dst: "", phase: "partial",
    });
    const promise = this.deps.demoReply({
      speaker_text: srcText,
      speaker_language: demo.speakerCode,
      listener_language: demo.listenerCode,
      voice_gender: demo.voiceGender ?? "female",
    }).then((r) => r).catch((e) => {
      this.set({ errBanner: e instanceof Error ? e.message : String(e) });
      return null;
    });
    this.demoReplyInflight = { id: fakeId, promise };
    // Fallback: if the owner TTS that normally triggers playback never
    // arrives (synthesis dropped server-side), play the reply anyway. The
    // guard makes this a no-op when the normal trigger already consumed it.
    this.setTimeoutFn(() => {
      if (this.demoReplyInflight?.id === fakeId) void this.playDemoGuestReply();
    }, DEMO_REPLY_FALLBACK_MS);
  }

  private async playDemoGuestReply(): Promise<void> {
    if (!this.demoMode) return;
    const inflight = this.demoReplyInflight;
    if (!inflight) return;
    this.demoReplyInflight = null;
    const reply = await inflight.promise;
    if (!reply) {
      this.set({ msgs: this.state.msgs.filter((m) => m.id !== inflight.id) });
      return;
    }
    this.patchMsg(inflight.id, { src: reply.reply_text, dst: reply.back_translation, phase: "final" });
    this.set({ speakingId: inflight.id });
    // Hold the half-duplex mute ourselves: this can now run outside an owner
    // `tts_audio` job (unspoken final / fallback timer), where no caller is
    // holding a TTS job open. The count makes the nested case harmless.
    this.beginTts();
    try { await this.deps.audio.enqueue(base64ToArrayBuffer(reply.audio_base64)); }
    catch { /* ignore */ }
    finally {
      this.endTts();
      if (this.state.speakingId === inflight.id) this.set({ speakingId: null });
    }
  }
}

/** Normalize text for duplicate detection (mirrors the server's `_norm_text`):
 *  drop punctuation/symbols, collapse whitespace, lower-case. */
export function normText(s: string): string {
  return s.replace(/[^\p{L}\p{N}\s]/gu, "").split(/\s+/).filter(Boolean).join(" ").toLowerCase();
}

/** Pick the best translation for `code`, tolerating locale-variant mismatches. */
export function pickTrans(translations: Record<string, string>, code: string): string {
  if (translations[code]) return translations[code];
  const base = code.split("-")[0]!;
  if (translations[base]) return translations[base];
  for (const [k, v] of Object.entries(translations)) {
    if (k.split("-")[0] === base) return v;
  }
  return "";
}

/** Decode a base64 string into a binary ArrayBuffer (server WAV payloads). */
export function base64ToArrayBuffer(b64: string): ArrayBuffer {
  const bin = atob(b64);
  const len = bin.length;
  const out = new Uint8Array(len);
  for (let i = 0; i < len; i++) out[i] = bin.charCodeAt(i);
  return out.buffer;
}
